import {
  Form,
  FormInstance,
  Radio,
  Checkbox,
  Flex,
  Input,
  Image,
  Space
} from 'antd'
import { useTheme } from 'antd-style'
import { useEffect, useState } from 'react'

import { QuestionResponse, QuestionType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { getPreview } from 'src/features/questionnaires/utils'

import { CustomCheckBox } from '../CustomCheckBox'

import { useStyles } from './styles'

type Props = {
  questionIndex?: string
  form?: FormInstance
  previewLang?: 'ro' | 'ru'
  question: QuestionResponse
}

export const AnswerCard = ({
  questionIndex = '0',
  previewLang = 'ro',
  form,
  question
}: Props) => {
  const [imagePreviews, setImagePreviews] = useState<{ [key: string]: string }>(
    {}
  )

  const token = useTheme()

  const isSingleChoice = question.type === QuestionType.SINGLE_CHOICE

  const customQuestionsId = question?.id ?? String(questionIndex + 1)

  const fieldName = ['answers', customQuestionsId, 'choices']

  const { styles } = useStyles()

  const selectedValues = Form.useWatch(fieldName, form)

  const onChange = (value: string | string[]) => {
    const currentAnswers = form?.getFieldValue('answers') || {}

    const newValues: string[] = Array.isArray(value) ? value : [value]

    const isOtherSelected =
      question?.hasOther && newValues.includes(String(question?.otherChoiceId))

    const existingAnswer = currentAnswers[customQuestionsId] || {}

    let updatedChoices: string[]

    if (isOtherSelected) {
      if (question.type === QuestionType.SINGLE_CHOICE) {
        updatedChoices = [String(question?.otherChoiceId)]
      } else {
        updatedChoices = [
          ...new Set([...newValues, String(question.otherChoiceId)])
        ]
      }
    } else {
      updatedChoices = newValues.filter(
        (choice) => choice !== question.otherChoiceId
      )
    }

    const newAnswers = {
      ...currentAnswers,
      [String(customQuestionsId)]: {
        choices: updatedChoices,
        otherText: isOtherSelected ? existingAnswer.otherText || '' : null,
        isOtherSelected
      }
    }

    form?.setFieldsValue({ answers: newAnswers })
  }

  const getBackgroundColor = (isSelected: boolean) => {
    return {
      backgroundColor: isSelected
        ? token.colorPrimary
        : token.lightGrey.lightGrey1
    }
  }

  const isSelected = (id?: string | number) =>
    form?.getFieldValue(['answers', customQuestionsId, 'choices'])?.includes(id)

  const hasOther = question?.hasOther
  const customOtherId = question?.otherChoiceId ?? 'other'

  const hasOtherText =
    previewLang === 'ro' ? 'Introduceți opțiunea dvs.' : 'Введите свой вариант'

  const hasOtherTextDescription =
    previewLang === 'ro' ? 'Descriere' : 'Описание'

  useEffect(() => {
    const fetchImagePreviews = async () => {
      if (!question?.answerChoices) return

      const previews: { [key: string]: string } = {}

      const promises = question.answerChoices.map(async (choice, index) => {
        const mediaFile = form?.getFieldValue([
          'questions',
          questionIndex,
          'answerChoices',
          index,
          'media'
        ])

        if (mediaFile?.mediaFile?.uid) {
          const fileUrl = await getPreview(mediaFile?.mediaFile?.uid)
          previews[choice?.id ?? index + 1] = fileUrl
        }
      })

      await Promise.all(promises)
      setImagePreviews(previews)
    }

    fetchImagePreviews()
  }, [question, form, questionIndex])

  return (
    <>
      <Form.Item name="answers" hidden />
      <Form.Item name={fieldName} valuePropName="value" rules={requiredRules}>
        {isSingleChoice ? (
          <Radio.Group
            className="full-width"
            onChange={(e) => onChange(e.target.value)}
            value={selectedValues}
          >
            <Flex vertical gap={16} className={styles.itemWrapper}>
              {question?.answerChoices?.map((choice, index) => {
                const customId = choice?.id ?? index + 1

                return (
                  <label key={customId}>
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={16}
                      className={styles.choice}
                      style={getBackgroundColor(isSelected(customId))}
                      onClick={() => onChange(String(customId))}
                    >
                      <Space>
                        <Radio value={customId} />
                        <Text
                          color={
                            isSelected(customId)
                              ? token.colorWhite
                              : token.colorText
                          }
                        >
                          {choice?.text?.[previewLang]}
                        </Text>
                      </Space>

                      {imagePreviews[customId] && (
                        <Image
                          src={imagePreviews[customId]}
                          className={styles.imagePreview}
                          preview={{
                            maskClassName: styles.imagePreviewMask
                          }}
                        />
                      )}
                    </Flex>
                  </label>
                )
              })}

              {hasOther && (
                <label key={customOtherId}>
                  <Flex
                    align="start"
                    gap={16}
                    className={styles.choice}
                    style={getBackgroundColor(
                      selectedValues?.includes(customOtherId)
                    )}
                    onClick={() => onChange(customOtherId)}
                  >
                    <Radio value={customOtherId} />
                    <Flex vertical gap={4} className="full-width">
                      <Text
                        color={
                          isSelected(customOtherId)
                            ? token.colorWhite
                            : token.colorText
                        }
                      >
                        {hasOtherText}
                      </Text>
                      <Input.TextArea
                        placeholder={hasOtherTextDescription}
                        rows={3}
                        onChange={(e) => {
                          form?.setFieldsValue({
                            answers: {
                              ...form?.getFieldValue('answers'),
                              [customQuestionsId]: {
                                ...form?.getFieldValue([
                                  'answers',
                                  customQuestionsId
                                ]),
                                otherText: e.target.value
                              }
                            }
                          })
                        }}
                        disabled={!selectedValues?.includes(customOtherId)}
                      />
                    </Flex>
                  </Flex>
                </label>
              )}
            </Flex>
          </Radio.Group>
        ) : (
          <Checkbox.Group
            onChange={(values) => onChange(values)}
            value={selectedValues}
            className="full-width"
          >
            <Flex vertical gap={16} className={styles.itemWrapper}>
              {question?.answerChoices?.map((choice, index) => {
                const customId = choice?.id ?? index + 1

                return (
                  <Flex
                    key={customId}
                    align="center"
                    justify="space-between"
                    gap={16}
                    className={styles.choice}
                    style={getBackgroundColor(isSelected(customId))}
                    onClick={() => {
                      const newValue = isSelected(customId)
                        ? selectedValues?.filter(
                            (id: string) => id !== customId
                          )
                        : [...(selectedValues || []), customId]
                      onChange(newValue)
                    }}
                  >
                    <CustomCheckBox
                      isSelected={isSelected(customId)}
                      value={customId}
                    >
                      <Text
                        className={styles.choiceText}
                        color={
                          isSelected(customId)
                            ? token.colorWhite
                            : token.colorText
                        }
                      >
                        {choice?.text?.[previewLang]}
                      </Text>
                    </CustomCheckBox>

                    {imagePreviews[customId] && (
                      <Image
                        src={imagePreviews[customId]}
                        className={styles.imagePreview}
                      />
                    )}
                  </Flex>
                )
              })}

              {hasOther && (
                <label key={customOtherId}>
                  <Flex
                    align="start"
                    gap={16}
                    className={styles.choice}
                    style={getBackgroundColor(
                      selectedValues?.includes(customOtherId)
                    )}
                    onClick={() => onChange(customOtherId)}
                  >
                    <CustomCheckBox
                      value={customOtherId}
                      isSelected={isSelected(customOtherId)}
                      style={{ alignSelf: 'start', flexShrink: 0 }}
                    />

                    <Flex vertical gap={4} className="full-width">
                      <Text
                        color={
                          isSelected(String(customOtherId))
                            ? token.colorWhite
                            : token.colorText
                        }
                      >
                        {hasOtherText}
                      </Text>
                      <Input.TextArea
                        placeholder={hasOtherTextDescription}
                        rows={3}
                        onChange={(e) => {
                          form?.setFieldsValue({
                            answers: {
                              ...form?.getFieldValue('answers'),
                              [customQuestionsId]: {
                                ...form?.getFieldValue([
                                  'answers',
                                  customQuestionsId
                                ]),
                                otherText: e.target.value
                              }
                            }
                          })
                        }}
                        disabled={!selectedValues?.includes(customOtherId)}
                      />
                    </Flex>
                  </Flex>
                </label>
              )}
            </Flex>
          </Checkbox.Group>
        )}
      </Form.Item>
    </>
  )
}
